/**
 * Kleine, abhängigkeitsfreie i18n-Lösung (DE/EN) für das Admin-UI.
 *
 * - `de` ist die Quelle der Wahrheit; `TranslationKey = keyof typeof de`.
 * - `en` ist als `Record<TranslationKey, string>` typisiert → TypeScript erzwingt,
 *   dass JEDER Key übersetzt ist (fehlender Key = Compile-Fehler).
 * - `t(key)` liest das aktive Sprach-Signal → Komponenten, die `t()` im Render
 *   aufrufen, re-rendern automatisch beim Sprachwechsel (kein Reload nötig).
 * - Sprache wird in `localStorage["bf2142.lang"]` persistiert.
 * - Default: explizite Wahl (localStorage) > Browser-Sprache (en* → EN) > DE.
 *   DE-Fallback, weil Inhalte/Team primär deutsch sind; eine bewusste Wahl gewinnt.
 */

import { signal } from "@preact/signals";

export type Lang = "de" | "en";

export const LANG_KEY = "bf2142.lang";

/** Auswahl für den Language-Switcher. */
export const LANGUAGES: { id: Lang; label: string }[] = [
  { id: "de", label: "DE" },
  { id: "en", label: "EN" },
];

/* ------------------------------------------------------------------ Wörterbuch */

const de = {
  // Navigation
  "nav.dashboard": "Dashboard",
  "nav.players": "Spieler",
  "nav.maps": "Karten",
  "nav.chat": "Chat",
  "nav.announcer": "Announcer",
  "nav.bans": "Sperren",
  "nav.commands": "Befehle",
  "nav.settings": "Einstellungen",
  "nav.console": "Konsole",
  "nav.playerIntel": "Spieler-Intel",
  "nav.agentLive": "Agent Live",
  "agentLive.sub": "Live-Eventstream des Agents (SSE)",
  "agentLive.statusConnecting": "Verbinde…",
  "agentLive.statusConnected": "Verbunden",
  "agentLive.statusError": "Getrennt",
  "agentLive.statusClosed": "Geschlossen",
  "agentLive.reconnecting": "Reconnect…",
  "agentLive.filters": "Filter",
  "agentLive.serverFilter": "Server-ID",
  "agentLive.typeFilter": "Typ",
  "agentLive.allTypes": "Alle",
  "agentLive.heartbeats": "Heartbeats / Host-Status",
  "agentLive.serverStatus": "Server-Status",
  "agentLive.statusOnline": "Online",
  "agentLive.statusStale": "Veraltet",
  "agentLive.statusOffline": "Offline",
  "agentLive.statusUnknown": "Unbekannt",
  "agentLive.agent": "Agent",
  "agentLive.cpu": "CPU",
  "agentLive.ram": "RAM",
  "agentLive.disk": "Datenträger",
  "agentLive.loadAvg": "Load",
  "agentLive.lastHeartbeat": "Letzter Heartbeat",
  "agentLive.noHeartbeats": "Keine Heartbeats empfangen",
  "agentLive.events": "Event-Stream",
  "agentLive.noEvents": "Noch keine Events.",
  "agentLive.queue": "Queue",
  "agentLive.uptime": "Uptime",
  "agentLive.lastEvent": "Letztes Event",
  "agentLive.eventsReceived": "Events empfangen",
  "agentLive.forbidden": "Keine Berechtigung für Agent Live.",
  "agentLive.killfeed": "Killfeed",
  "agentLive.noKills": "Keine Kills empfangen",
  "agentLive.kfHeadshot": "Headshot",
  "agentLive.kfSuicide": "Selbstkill",
  "agentLive.kfEnvironment": "Umgebung",
  "agentLive.kfUnknownWeapon": "Unbekannte Waffe",
  "killfeedPanel.title": "Killfeed",
  "killfeedPanel.empty": "Noch keine Killfeed-Events",
  "killfeedPanel.offline": "Agent offline – kein Live-Killfeed",
  "killfeedPanel.reset": "Position zurücksetzen",
  "killfeedPanel.collapse": "Einklappen",
  "killfeedPanel.expand": "Ausklappen",
  "dashboard.agent": "Agent",
  "dashboard.agentNone": "Kein Agent verbunden",
  "nav.log": "Log",
  "nav.auditLog": "Audit-Log",
  "nav.users": "Benutzer & Rollen",
  "nav.ingameAdmins": "Ingame Admins",
  "nav.servers": "Server",
  // Bewusst auch im Deutschen "Administration" (nicht "Verwaltung") — frühere
  // Design-Entscheidung, siehe ui-permissions-Test.
  "nav.administration": "Administration",

  // App-Shell / Topbar
  "shell.commandTerminal": "Command Terminal",
  "shell.rconControl": "RCON Control",
  "shell.menu": "Menü",
  "shell.noServer": "— kein Server —",
  "shell.offlineSuffix": "offline",
  "shell.connected": "Verbunden",
  "shell.connecting": "Verbinde…",
  "shell.offline": "Offline",
  "shell.logout": "Abmelden",
  "shell.theme": "Theme wechseln",
  "shell.themeDark": "Dunkel",
  "shell.themeLight": "Hell",
  "theme.dark": "Dunkel",
  "theme.light": "Hell",
  "theme.bloodForest": "Blood Forest",
  "theme.aurora": "Aurora",
  "theme.synth": "Synth",
  "shell.language": "Sprache",
  "shell.comingSoonTitle": "Diese Ansicht folgt im nächsten Slice.",
  "shell.comingSoon": "Kommt bald.",

  // Gemeinsame Buttons / Vokabular
  "common.save": "Speichern",
  "common.refresh": "Aktualisieren",
  "common.add": "Hinzufügen",
  "common.remove": "Entfernen",
  "common.reload": "Neu laden",
  "common.search": "Suchen",
  "common.cancel": "Abbrechen",
  "common.close": "Schließen",
  "common.online": "Online",
  "common.offline": "Offline",
  "common.live": "Live",
  "common.auto": "Auto",
  "common.players": "Spieler",
  "common.map": "Karte",
  "common.mode": "Modus",
  "common.size": "Größe",
  "common.lastSignal": "Letztes Signal",
  "common.loading": "Lädt…",
  "common.none": "Keine",

  // Login
  "login.secureTerminal": "SECURE TERMINAL · 2142",
  "login.subtitle": "BATTLEFIELD 2142 · NORTHERN STRIKE · RCON",
  "login.user": "Benutzer",
  "login.password": "Passwort",
  "login.submit": "Anmelden",
  "login.errorFailed": "Anmeldung fehlgeschlagen",
  "login.errorUnreachable": "Dienst nicht erreichbar",

  // Dashboard
  "dashboard.title": "Dashboard",
  "dashboard.serversInScope": "Server im Zugriff · Kacheln anpassbar",
  "dashboard.edit": "Anpassen",
  "dashboard.editDone": "Fertig",
  "dashboard.hostTelemetry": "Host-Telemetrie",
  "dashboard.liveCacheMatrix": "Live-Cache-Matrix",
  "dashboard.cpuLoad": "CPU / Last",
  "dashboard.ram": "RAM",
  "dashboard.disk": "Disk",
  "dashboard.rconQpm": "RCON QPM",
  "dashboard.unavailable": "nicht verfügbar",
  "dashboard.colServer": "Server",
  "dashboard.colState": "Status",
  "dashboard.colMapMode": "Karte / Modus",
  "dashboard.colPlayers": "Spieler",
  "dashboard.colCacheAge": "Cache-Alter",
  "dashboard.colRconQpm": "RCON QPM",
  "dashboard.colRconHealth": "RCON-Health",
  "dashboard.colLastSignal": "Letztes Signal",
  "dashboard.noServers": "Keine Server im Zugriff.",
  "dashboard.loadingHost": "Control-Host-Telemetrie wird geladen.",
  "dashboard.gameHostNotConnected": "Game-Server Host-Metriken: nicht angebunden",

  // Zentrale View-Titel
  "view.players": "Spieler",
  "view.playerIntel": "Spieler-Intel",
  "view.playerSearch": "Spieler-Suche",
  "view.playerDetail": "Spieler-Detail",
  "view.maps": "Karten",
  "view.auditLog": "Audit-Log",
  "view.users": "Benutzer & Rollen",
  "view.servers": "Server",
  "view.noServerSelected": "Kein Server ausgewählt.",

  // Gemeinsames Vokabular (Maps/Players)
  "common.error": "Fehler",
  "common.apply": "Anwenden",
  "common.set": "Setzen",
  "common.load": "Laden",
  "common.command": "Befehl",
  "field.rank": "Rang",
  "field.player": "Spieler",
  "field.action": "Aktion",
  "field.actions": "Aktionen",
  "status.on": "AN",
  "status.off": "AUS",

  // Maps-View
  "maps.sub": "Maplist, Rundensteuerung, Tickets & Flag-by-Flag.",
  "maps.toastRestarted": "Map neu gestartet",
  "maps.nextMap": "Nächste Map",
  "maps.toastNextLoaded": "Nächste Map geladen",
  "maps.currentMaplist": "Aktuelle Maplist",
  "maps.emptyRotation": "Keine Maps in der Rotation. Unten eine Map hinzufügen.",
  "maps.rawOutput": "Rohausgabe",
  "maps.toastSaved": "Maplist gespeichert",
  "maps.toastTicketsSet": "Tickets gesetzt",
  "maps.toastTicketsDoubled": "Tickets verdoppelt",
  "maps.doubleTitle": "Beide Teams verdoppeln",
  "maps.fbfOn": "Einschalten",
  "maps.fbfOff": "Ausschalten",
  "maps.fbfEnabled": "Flag-by-Flag an",
  "maps.fbfDisabled": "Flag-by-Flag aus",
  "maps.addMap": "Map hinzufügen",
  "maps.append": "Anhängen",
  "maps.toastMapAdded": "Map hinzugefügt (mapList.save nicht vergessen)",
  "maps.toastRemoved": "Eintrag entfernt (mapList.save nicht vergessen)",
  "maps.loadNow": "Jetzt laden",
  "maps.loadNowTitle": "Sofort zu dieser Map wechseln (beendet die aktuelle Runde)",
  "maps.toastPickMap": "Erst eine Map wählen",
  "maps.switchTo": "Jetzt wechseln zu",
  "maps.roundEnds": "Die aktuelle Runde endet.",
  "maps.switching": "Wechsle zu",
  "maps.setNextMap": "Nächste Map setzen",
  "maps.nextMapIndex": "Nächste Map = Index",
  "maps.removeEntry": "Eintrag entfernen",

  // Players-View
  "players.empty": "Keine Spieler.",
  "players.kickTitle": "Spieler kicken",
  "players.banTitle": "Spieler bannen",
  "players.banEngine": "per Server-/Engine-Ban bannen",
  "players.slotId": "Slot-ID",
  "players.copy": "kopieren",
  "players.copied": "kopiert",
  "players.copyFailed": "konnte nicht kopiert werden",
  "players.kickSent": "Kick gesendet",
  "players.banSent": "Ban gesendet",
  "players.kickFailed": "Kick fehlgeschlagen",
  "players.banFailed": "Ban fehlgeschlagen",
  "players.warn": "Warnen",
  "players.warnTitle": "Spieler warnen",
  "players.warnPrompt": "Warnung an",
  "players.warnSent": "Warnung gesendet",
  "players.warnFailed": "Warnung fehlgeschlagen",
  "players.banNoPid": "Keine PID — Ban hier nicht möglich",
  "players.banPidConfirm": "per PID-Ban bannen? (panel-erzwungen, Kick-on-Join)",
  "players.banReasonPrompt": "Grund (optional):",
  "players.autobalance": "Autobalance",
  "players.abOn": "Autobalance an",
  "players.abOff": "Autobalance aus",
  "players.noLiveData": "Keine Live-Daten — Server/Worker nicht verbunden.",
  "players.of": "von",
  "players.statTickets": "Tickets",
  "players.tacControls": "Tactical-Map-Steuerung",
  "players.tacObjects": "Objekte",
  "players.tacCalibrate": "Kalibrieren",
  "players.tacFitTitle": "Auto-Zoom auf Objekte",
  "players.tacObjectsTitle": "Static-Objekte ein/aus",
  "players.tacCropTitle": "Bildausschnitt im Editor ziehen",
  "players.tacCalibrateTitle": "3 Punkte im Editor kalibrieren",
  "players.tacEditorTitle": "Großen Kalibrier-Editor öffnen",
  "players.tacCloseTitle": "Schließen (ESC)",
  "players.tacCropHint": "Crop: Rechteck über den Bereich ziehen (auto-gespeichert); winziger Drag = Reset",
  "players.tacCropEnd": "Crop-Editor beendet",
  "players.tacCalHint": "Kalibrierung: ingame auf einen Punkt stellen, dann hier anklicken (3×)",
  "players.tacCalCancel": "Kalibrierung abgebrochen",
  "players.tacCropSaved": "Ausschnitt gespeichert ✓",
  "players.tacCropReset": "Ausschnitt zurückgesetzt",
  "players.tacSaveFailed": "Speichern fehlgeschlagen",
  "players.tacPosFrom": "Position von",
  "players.tacPosCaptured": "erfasst",
  "players.tacNoWorld": "Keine Weltkoordinaten",
  "players.tacCalSaved": "Kalibrierung gespeichert ✓",
  "players.kickVerb": "kicken",
  "players.tacCropStatus": "CROP — Rechteck über den Bereich ziehen · winziger Drag = Reset",
  "players.tacCalStatus": "CALIBRATE — ingame auf einen Spot stellen, dann anklicken · Punkt",
  "players.tacPoint": "Punkt",
  "players.tacWhoOnSpot": "wer steht auf diesem Spot?",
  "players.tacPickNumber": "Nummer — oder X,Z manuell:",
  "players.tacNoLiveCoords": "keine Live-Koordinaten.",
  "players.tacManualXZ": "Welt X,Z manuell (z.B. \"358,50\"):",
  "players.tacFitFlagTitle": "Fit zeigt die ganze Objekt-BBox – ausschalten für den Wide-Ausschnitt",

  // Geteiltes Vokabular (Player Intel / Audit)
  "common.reset": "Zurücksetzen",
  "status.visible": "sichtbar",
  "status.hidden": "verborgen",

  // Player Intel
  "playerIntel.sub": "Dauerhafte Player-Historie aus Live-Polls, Chat-History und GeoIP-Cache. IP/Geo bleiben permission-gated.",
  "playerIntel.searchPlaceholder": "PID, Name oder ClanTag suchen",
  "playerIntel.searchPlaceholderIp": "PID, Name, ClanTag oder IP suchen",
  "playerIntel.loadingList": "Lade Player-Historie…",
  "playerIntel.noPlayers": "Keine Player gefunden.",
  "playerIntel.noTag": "KEIN TAG",
  "playerIntel.seenLower": "gesehen",
  "playerIntel.loadFailed": "Player Intel konnte nicht geladen werden.",
  "playerIntel.detailLoadFailed": "Player konnte nicht geladen werden.",
  "playerIntel.selectPlayer": "Player auswählen, um History und Verknüpfungen zu prüfen.",
  "playerIntel.lastServer": "Letzter Server",
  "playerIntel.lastSeen": "Zuletzt gesehen",
  "playerIntel.firstSeen": "Zuerst gesehen",
  "playerIntel.seen": "Gesehen",
  "playerIntel.seenCount": "Sichtungen",
  "playerIntel.vehicle": "Fahrzeug",
  "playerIntel.kit": "Kit",
  "playerIntel.current": "Aktuell",
  "playerIntel.nameHistory": "Namens-Historie",
  "playerIntel.clanTagHistory": "ClanTag-Historie",
  "playerIntel.serverHistory": "Server-Historie",
  "playerIntel.ipHistory": "IP-Historie",
  "playerIntel.chatHistory": "Chat-Historie",
  "playerIntel.noStoredPre": "Keine",
  "playerIntel.noStoredPost": "gespeichert.",
  "playerIntel.noServerHistory": "Keine Server-Historie gespeichert.",
  "playerIntel.noIpHistory": "Keine IP-Historie gespeichert.",
  "playerIntel.noChatLines": "Keine Chat-Zeilen verknüpft.",
  "playerIntel.ipRequiresPerm": "IP-Historie erfordert action.view_ip.",
  "playerIntel.chatRequiresPerm": "Chat-Historie erfordert view.chat_history.",
  "playerIntel.requiresViewGeo": "erfordert action.view_geo",
  "playerIntel.geoNotConfigured": "GeoIP nicht konfiguriert",
  "playerIntel.geoDbMissing": "GeoIP-DB fehlt",
  "playerIntel.refreshGeo": "GeoIP aktualisieren",
  "playerIntel.geoLooking": "GeoIP wird aufgelöst…",

  // Audit Log
  "audit.sub": "DB-gestütztes Audit-Protokoll (auth, rcon, bans, settings …) — getrennt vom App/File-Log im Log-Tab.",
  "audit.filter": "Filter",
  "audit.events": "Events",
  "audit.entry": "Eintrag",
  "audit.selectRow": "Eine Zeile wählen, um Details zu prüfen.",
  "audit.time": "Zeit",
  "audit.result": "Ergebnis",
  "audit.actor": "Akteur",
  "audit.target": "Ziel",
  "audit.summary": "Zusammenfassung",
  "audit.serverId": "Server-ID",
  "audit.range": "Zeitraum",
  "audit.textSearch": "Textsuche (Client)",
  "audit.actionPlaceholder": "z. B. auth.login",
  "audit.actorPlaceholder": "Benutzername",
  "audit.serverIdPlaceholder": "z. B. ns-ranked",
  "audit.textPlaceholder": "Action, Akteur, Ziel, Zusammenfassung…",
  "audit.all": "Alle",
  "audit.range1h": "1 h",
  "audit.range24h": "24 h",
  "audit.range7d": "7 Tage",
  "audit.range30d": "30 Tage",
  "audit.loading": "Lade Audit-Log…",
  "audit.empty": "Keine Einträge.",
  "audit.loadFailed": "Audit Log konnte nicht geladen werden.",
  "audit.noMetadata": "Keine Metadaten.",
  "audit.sensitiveHintPre": "IP / User-Agent erfordern action.view_sensitive. Sensible Werte sind serverseitig als",
  "audit.sensitiveHintPost": "markiert.",

  // Geteiltes Vokabular (finaler View-Sweep)
  "common.send": "Senden",
  "common.clear": "Leeren",
  "common.execute": "Ausführen",
  "common.editAction": "Bearbeiten",
  "common.delete": "Löschen",
  "common.saved": "Gespeichert",
  "common.deleted": "Gelöscht",
  "common.noChanges": "Keine Änderungen",
  "field.name": "Name",
  "field.reason": "Grund",

  // Log
  "log.sub": "Letzte 250 protokollierte Aktionen.",

  // Console
  "console.sub": "Freier RCON-Befehl (exec). Destruktive Befehle nur für Admins.",
  "console.ready": "Bereit. Befehl unten eingeben…",
  "console.noOutput": "(keine Ausgabe)",

  // Chat
  "chat.sub": "Live-Chat des Servers (über den Worker). Broadcasts an alle/Team.",
  "chat.history": "Verlauf",
  "chat.empty": "Noch keine Chatzeilen. (Worker muss laufen.)",
  "chat.messagePlaceholder": "Nachricht…",
  "chat.toTeam": "An Team",
  "chat.toAll": "An alle",
  "chat.broadcastSent": "Broadcast gesendet",
  "chat.sentToTeam": "An Team gesendet",

  // Announcer
  "announcer.sub": "Rotierende game.sayAll-Nachrichten.",
  "announcer.messages": "Nachrichten",
  "announcer.addMessage": "+ Nachricht",
  "announcer.empty": "Noch keine Nachrichten.",
  "announcer.maxHint": "Max. 20 Nachrichten; leere werden beim Speichern entfernt.",
  "announcer.enabled": "Aktiviert",
  "announcer.intervalLabel": "Intervall (Sekunden, min. 10)",
  "announcer.saved": "Announcer gespeichert",
  "nav.joinAnnouncer": "Join Announcer",
  "joinAnnouncer.sub": "Join-/Leave-Ansagen pro Server konfigurieren (game.sayAll).",
  "joinAnnouncer.settings": "Einstellungen",
  "joinAnnouncer.templates": "Templates",
  "joinAnnouncer.variables": "Variablen",
  "joinAnnouncer.variablesHint": "In die Templates einsetzbar. Fehlende Werte nutzen sinnvolle Defaults.",
  "joinAnnouncer.default": "Default",
  "joinAnnouncer.preview": "Vorschau",
  "joinAnnouncer.previewJoin": "Join:",
  "joinAnnouncer.previewLeave": "Leave:",
  "joinAnnouncer.previewEmpty": "Nichts zu zeigen.",
  "joinAnnouncer.previewNote": "Vorschau mit Beispielspieler. Der Worker übernimmt Änderungen nach spätestens 30 Sekunden.",
  "joinAnnouncer.joinEnabled": "Join-Ansagen aktiv",
  "joinAnnouncer.leaveEnabled": "Leave-Ansagen aktiv",
  "joinAnnouncer.statsEnabled": "Stats-Zeilen aktiv",
  "joinAnnouncer.colorEnabled": "Farbe aktiv",
  "joinAnnouncer.cooldown": "Cooldown (Sekunden)",
  "joinAnnouncer.cooldownHint": "Verhindert Spam bei Reconnect/Refresh (pro PID).",
  "joinAnnouncer.joinLine1": "Join-Zeile 1",
  "joinAnnouncer.joinLine2": "Join-Zeile 2 (Stats)",
  "joinAnnouncer.joinLine3": "Join-Zeile 3 (Stats)",
  "joinAnnouncer.leaveTemplate": "Leave-Nachricht",
  "joinAnnouncer.statsLineHint": "Zeile 2/3 nur, wenn Stats aktiv UND verfügbar — sonst nur Zeile 1.",
  "joinAnnouncer.joinOffOrEmpty": "Join aus oder Zeilen leer.",
  "joinAnnouncer.leaveOffOrEmpty": "Leave aus oder Nachricht leer.",
  "joinAnnouncer.reset": "Zurücksetzen",
  "joinAnnouncer.retry": "Erneut versuchen",
  "joinAnnouncer.loading": "Einstellungen werden geladen…",
  "joinAnnouncer.loadError": "Einstellungen konnten nicht geladen werden.",
  "joinAnnouncer.readonly": "Nur-Lese-Zugriff (action.edit_announcer fehlt) — Speichern deaktiviert.",
  "joinAnnouncer.saved": "Join-Announcer gespeichert",

  // Commands
  "commands.sub": "Vordefinierte Befehlsgruppen. Toggles merken Autoload für Map-Start.",
  "commands.autoTitle": "Bei Map-Start automatisch erneut anwenden",
  "commands.confirmRunPre": "„",
  "commands.confirmRunPost": "\" ausführen?",
  "commands.sentSuffix": "gesendet",
  "commands.commandsWord": "Befehl(e)",
  "commands.loading": "Befehlsgruppen werden geladen…",
  "commands.loadError": "Befehlsgruppen konnten nicht geladen werden.",
  "commands.retry": "Erneut versuchen",
  "commands.empty": "Keine Befehlsgruppen konfiguriert.",

  // Settings
  "settings.sub": "sv.*-Servereinstellungen (live, nur Admins).",
  "settings.savedCountSuffix": "Einstellung(en) gespeichert",

  // Bans
  "bans.sub": "Panel PID Bans, Known PIDs und getrennte Engine/RCON-Banlisten.",
  "bans.pidBanExplanation": "PID-Bans werden vom Panel/Worker erzwungen. Wenn diese PID online auftaucht, wird der Spieler automatisch gekickt.",
  "bans.activePidBans": "Aktive PID-Bans",
  "bans.panelWorkerEnforced": "Panel/Worker-erzwungen",
  "bans.reasonRequiredPh": "Grund erforderlich",
  "bans.noPidBans": "Keine PID-Bans.",
  "bans.by": "Von",
  "bans.created": "Erstellt",
  "bans.knownPids": "Bekannte PIDs",
  "bans.knownPidsOffline": "Bekannte PIDs / Offline-Ban",
  "bans.knownNote": "Quelle: seen_players. Suche nach Name oder PID; IP-Suche ist hier bewusst nicht enthalten.",
  "bans.searchNamePid": "Name oder PID suchen…",
  "bans.noResults": "Keine Ergebnisse.",
  "bans.lastSeenShort": "Zuletzt",
  "bans.hitsSuffix": "Treffer",
  "bans.engineRconIp": "Engine / RCON IP",
  "bans.engineRconBanIp": "Engine / RCON Ban: IP",
  "bans.engineNote": "RCON/serverseitige Adress-Banliste. Nicht dasselbe wie ein Panel/Worker-erzwungener PID Ban.",
  "bans.ipAddress": "IP-Adresse",
  "bans.ban": "Bannen",
  "bans.unban": "Entbannen",
  "bans.ingameAdminsTitle": "Ingame-Admins",
  "bans.legacyWhitelistReplaced": "Legacy-Whitelist wurde durch Ingame Admins ersetzt.",
  "bans.ingameAdminsHint": "In-Game-Rechte (#k/#w/#b/#ab/…) werden unter »Administration → Ingame Admins« gepflegt.",
  "bans.openIngameAdmins": "→ Ingame Admins",
  "bans.pidReasonRequired": "PID und Grund erforderlich",
  "bans.pidBanned": "PID gebannt",
  "bans.pidUnbanned": "PID entbannt",
  "bans.offlineBanSet": "Offline PID-Ban gesetzt",
  "bans.ipBanned": "IP gebannt",
  "bans.ipUnbanned": "IP entbannt",
  "bans.confirmRemovePidPre": "PID-Ban",
  "bans.confirmRemovePidPost": "entfernen?",
  "bans.banReasonPrompt": "Bann-Grund für",
  "bans.banReasonPromptSuffix": "(erforderlich):",
  "bans.confirmCreatePre": "PID-Ban für",
  "bans.confirmCreatePost": "erstellen?",
  "bans.reasonLabel": "Grund",

  // Servers
  "servers.sub": "RCON-Zugänge verwalten. Passwörter werden nie angezeigt.",
  "servers.addServer": "+ Neuer Server",
  "servers.newServer": "Neuer Server",
  "servers.editSuffix": "bearbeiten",
  "servers.pwSet": "gesetzt",
  "servers.pwMissing": "fehlt",
  "servers.hostIp": "Host / IP",
  "servers.rconPassword": "RCON-Passwort",
  "servers.emptyUnchanged": "(leer = unverändert)",
  "servers.idAutoHint": "Die Server-ID wird automatisch aus dem Namen erzeugt.",
  "servers.confirmDeletePre": "Server „",
  "servers.confirmDeletePost": "\" löschen? Zuweisungen werden entfernt.",

  // Users
  "users.sub": "Konten, Rollen und Server-Zuweisung.",
  "usersRoles.sub": "Accounts, Rollen und Berechtigungen.",
  "usersRoles.tabUsers": "Benutzer",
  "usersRoles.tabRoles": "Rollen",
  "usersRoles.tabPermissions": "Permissions",
  "usersRoles.usersTitle": "Benutzer",
  "usersRoles.rolesTitle": "Rollen",
  "usersRoles.permissionsTitle": "Permission-Katalog",
  "usersRoles.viewPermissions": "Rechte",
  "usersRoles.effectiveTitle": "Effektive Rechte",
  "usersRoles.legacyAdminNote": "Legacy-Admin-Rolle → alle Permissions",
  "usersRoles.granularRoles": "Granulare Rollen",
  "usersRoles.noRoles": "Keine Rollen zugewiesen",
  "usersRoles.viewerFallback": "Keine Rollen → konservativer Viewer-Fallback",
  "usersRoles.directOverrides": "Direkte Overrides",
  "usersRoles.effectivePermissions": "Effektive Permissions",
  "usersRoles.roleName": "Rolle",
  "usersRoles.userCount": "User",
  "usersRoles.permissionCount": "Rechte",
  "usersRoles.rolesReadonlyNote": "Rolle anklicken, um ihre Permissions zu bearbeiten.",
  "usersRoles.permissionsReadonlyNote": "Nur Katalog-Anzeige — Zuweisung über Rollen/User.",
  "usersRoles.noPassword": "kein Passwort",
  "usersRoles.matrixTitle": "Permission-Matrix",
  "usersRoles.selectRoleHint": "Rolle in der Liste anklicken, um ihre Permissions zu sehen/bearbeiten.",
  "usersRoles.systemRoleNote": "Systemrolle — nicht editierbar.",
  "usersRoles.noManageRoles": "Nur lesbar — fehlende Berechtigung (action.manage_roles).",
  "usersRoles.savePermissions": "Permissions speichern",
  "usersRoles.unsavedChanges": "Ungespeicherte Änderungen",
  "usersRoles.noChanges": "Keine Änderungen",
  "usersRoles.permsSaved": "Permissions gespeichert",
  "usersRoles.added": "hinzugefügt",
  "usersRoles.removed": "entfernt",
  "usersRoles.assignRolesTitle": "Rollen-Zuweisung",
  "usersRoles.legacyRole": "Legacy-Rolle",
  "usersRoles.legacyAdminAssignNote": "Legacy-Admin hat alle Berechtigungen. Zugewiesene Rollen wirken hier nicht einschränkend.",
  "usersRoles.needBothManage": "Benötigt action.manage_users und action.manage_roles.",
  "usersRoles.systemBadge": "Systemrolle",
  "usersRoles.systemRolesNotAssignable": "Systemrollen sind hier nicht zuweisbar.",
  "usersRoles.saveRoles": "Rollen speichern",
  "usersRoles.rolesSaved": "Rollen gespeichert",
  "usersRoles.noAssignableRoles": "Keine zuweisbaren Rollen.",
  "usersRoles.resetPassword": "Passwort zurücksetzen",
  "usersRoles.resetConfirmHint": "Das temporäre Passwort wird nur einmal angezeigt und bleibt gültig, bis es geändert oder erneut zurückgesetzt wird.",
  "usersRoles.resetConfirmAction": "Zurücksetzen",
  "usersRoles.pwResetDone": "Passwort zurückgesetzt",
  "usersRoles.pwResetOnce": "Dieses Passwort wird nicht erneut angezeigt. Es bleibt gültig, bis es geändert oder erneut zurückgesetzt wird.",
  "usersRoles.copy": "Kopieren",
  "usersRoles.copied": "Kopiert",
  "usersRoles.copyManual": "Bitte manuell markieren und kopieren.",
  "usersRoles.tempPasswordTitle": "Temporäres Passwort",
  "usersRoles.createUser": "User anlegen",
  "usersRoles.createSubmit": "Anlegen",
  "usersRoles.optional": "(optional)",
  "usersRoles.userCreated": "User angelegt",
  "usersRoles.createHint": "Ein temporäres Passwort wird automatisch generiert. Es wird nur einmal angezeigt und bleibt gültig, bis es geändert oder erneut zurückgesetzt wird.",
  "users.addUser": "+ Neuer User",
  "users.newUser": "Neuer User",
  "users.editSuffix": "bearbeiten",
  "users.role": "Rolle",
  "users.you": " · du",
  "users.allServers": "alle Server",
  "users.nServersSuffix": "Server",
  "users.displayName": "Anzeigename",
  "users.password": "Passwort",
  "users.serverAccess": "Server-Zugriff",
  "users.adminAllServers": "Admins haben Zugriff auf alle Server.",
  "users.confirmDeletePre": "Account „",
  "users.confirmDeletePost": "\" löschen?",

  // Ingame Admins (PID-basierte Ingame-Moderatoren, kein Web-Login)
  "ingameAdmins.title": "Ingame Admins",
  "ingameAdmins.sub": "PID-basierte Ingame-Moderatoren (kein Web-Login). Rechte gelten ingame über Chat-Commands.",
  "ingameAdmins.list": "Ingame Admins",
  "ingameAdmins.empty": "Keine Ingame-Admins angelegt.",
  "ingameAdmins.add": "+ Neuer Ingame-Admin",
  "ingameAdmins.new": "Neuer Ingame-Admin",
  "ingameAdmins.editSuffix": "bearbeiten",
  "ingameAdmins.label": "Name / Label",
  "ingameAdmins.role": "Rolle",
  "ingameAdmins.scope": "Server-Scope",
  "ingameAdmins.permissions": "Rechte",
  "ingameAdmins.status": "Status",
  "ingameAdmins.lastSeen": "Zuletzt gesehen",
  "ingameAdmins.active": "Aktiv",
  "ingameAdmins.inactive": "Inaktiv",
  "ingameAdmins.allServers": "Alle Server",
  "ingameAdmins.nServersSuffix": "Server",
  "ingameAdmins.enabled": "Aktiviert",
  "ingameAdmins.notes": "Notizen",
  "ingameAdmins.scopeAll": "Alle Server",
  "ingameAdmins.scopeSpecific": "Bestimmte Server",
  "ingameAdmins.disable": "Deaktivieren",
  "ingameAdmins.enable": "Aktivieren",
  "ingameAdmins.rolePreset": "Rollen-Preset",
  "ingameAdmins.roleHelper": "Helper",
  "ingameAdmins.roleModerator": "Moderator",
  "ingameAdmins.roleAdmin": "Admin",
  "ingameAdmins.roleCustom": "Custom",
  "ingameAdmins.permWarn": "Warnen",
  "ingameAdmins.permSay": "Say",
  "ingameAdmins.permKick": "Kick",
  "ingameAdmins.permTempBan": "Temp-Ban",
  "ingameAdmins.permPidBan": "PID-Ban",
  "ingameAdmins.permAutobalance": "Autobalance",
  "ingameAdmins.permRestart": "Map-Neustart",
  "ingameAdmins.permNextMap": "Nächste Map",
  "ingameAdmins.permTickets": "Tickets setzen",
  "ingameAdmins.permFbf": "Flag-by-Flag",
  "ingameAdmins.permDoubleTickets": "Doppel-Tickets",
  "ingameAdmins.import": "Legacy importieren",
  "ingameAdmins.importTitle": "chat_admins / ban_admins → Ingame Admins importieren",
  "ingameAdmins.toastSaved": "Ingame-Admin gespeichert",
  "ingameAdmins.toastDeleted": "Ingame-Admin gelöscht",
  "ingameAdmins.toastEnabled": "Ingame-Admin aktiviert",
  "ingameAdmins.toastDisabled": "Ingame-Admin deaktiviert",
  "ingameAdmins.toastImported": "Import abgeschlossen",
  "ingameAdmins.confirmDeletePre": "Ingame-Admin PID ",
  "ingameAdmins.confirmDeletePost": " löschen?",
  "ingameAdmins.confirmImport": "Legacy chat_admins/ban_admins jetzt importieren? (idempotent, überschreibt bestehende Einträge nicht)",
  "ingameAdmins.pidPlaceholder": "PID (nur Ziffern)",
  "ingameAdmins.labelPlaceholder": "z. B. Clantag oder Realname",
  // Datei-Explorer
  "nav.files": "Dateien",
  "files.title": "Dateien",
  "files.sub": "Gameserver-Dateien über Agent oder SFTP – überall lesen, schreiben nur in freigegebenen Ordnern",
  "files.server": "Server",
  "files.noServers": "Kein Agent-Server mit Datei-Explorer verfügbar (Agent-Config: fs_enabled).",
  "files.agentOffline": "Agent nicht verbunden",
  "files.readOnly": "Nur lesen",
  "files.writable": "Schreiben erlaubt",
  "files.writeDirs": "Freigegebene Ordner",
  "files.noWriteDirs": "Keine – der Explorer ist komplett schreibgeschützt",
  "files.name": "Name",
  "files.modified": "Geändert",
  "files.empty": "Ordner ist leer",
  "files.truncated": "Liste gekürzt – zu viele Einträge",
  "files.up": "Eine Ebene hoch",
  "files.newFolder": "Neuer Ordner",
  "files.newFolderPrompt": "Name des neuen Ordners:",
  "files.uploadFiles": "Dateien hochladen",
  "files.uploadFolder": "Ordner hochladen",
  "files.overwrite": "Vorhandene überschreiben",
  "files.uploadDisabled": "Upload hier nicht erlaubt – nur in freigegebenen Ordnern",
  "files.noWritePermission": "Keine Schreibberechtigung (action.files_write)",
  "files.dropHint": "Dateien hierher ziehen zum Hochladen",
  "files.open": "Öffnen",
  "files.download": "Download",
  "files.extract": "Entpacken",
  "files.extractConfirm": "ZIP in diesen Ordner entpacken?",
  "files.rename": "Umbenennen",
  "files.renamePrompt": "Neuer Name:",
  "files.deleteConfirmPre": "Wirklich löschen: ",
  "files.deleteConfirmDirSuffix": " (Ordner inkl. gesamtem Inhalt)",
  "files.unsaved": "ungespeichert",
  "files.readOnlyFile": "schreibgeschützt",
  "files.discardConfirm": "Ungespeicherte Änderungen verwerfen?",
  "files.saved": "Gespeichert",
  "files.transfers": "Übertragungen",
  "files.statusUploading": "lädt hoch",
  "files.statusTransferring": "überträgt zum Server",
  "files.statusDownloading": "wird vom Server geholt",
  "files.statusDone": "fertig",
  "files.statusFailed": "Übertragung fehlgeschlagen",
  "files.clearDone": "Erledigte ausblenden",
  "files.link": "Verknüpfung",
  "files.extracted": "Entpackt",
  "files.created": "Angelegt",
  "files.renamed": "Umbenannt",
  "files.deleted": "Gelöscht",
  "files.viaAgent": "über Agent",
  "files.viaSftp": "über SFTP (Fallback)",
  "files.viaSftpHint": "Agent nicht verbunden – Zugriff läuft über den SFTP-Container",
  "files.sftpConfigErrors": "SFTP-Konfiguration fehlerhaft",
} as const;

export type TranslationKey = keyof typeof de;

const en: Record<TranslationKey, string> = {
  // Navigation
  "nav.dashboard": "Dashboard",
  "nav.players": "Players",
  "nav.maps": "Maps",
  "nav.chat": "Chat",
  "nav.announcer": "Announcer",
  "nav.bans": "Bans",
  "nav.commands": "Commands",
  "nav.settings": "Settings",
  "nav.console": "Console",
  "nav.playerIntel": "Player Intel",
  "nav.agentLive": "Agent Live",
  "agentLive.sub": "Live agent event stream (SSE)",
  "agentLive.statusConnecting": "Connecting…",
  "agentLive.statusConnected": "Connected",
  "agentLive.statusError": "Disconnected",
  "agentLive.statusClosed": "Closed",
  "agentLive.reconnecting": "Reconnecting…",
  "agentLive.filters": "Filters",
  "agentLive.serverFilter": "Server ID",
  "agentLive.typeFilter": "Type",
  "agentLive.allTypes": "All",
  "agentLive.heartbeats": "Heartbeats / host status",
  "agentLive.serverStatus": "Server status",
  "agentLive.statusOnline": "Online",
  "agentLive.statusStale": "Stale",
  "agentLive.statusOffline": "Offline",
  "agentLive.statusUnknown": "Unknown",
  "agentLive.agent": "Agent",
  "agentLive.cpu": "CPU",
  "agentLive.ram": "RAM",
  "agentLive.disk": "Disk",
  "agentLive.loadAvg": "Load avg",
  "agentLive.lastHeartbeat": "Last heartbeat",
  "agentLive.noHeartbeats": "No heartbeats received",
  "agentLive.events": "Event stream",
  "agentLive.noEvents": "No events yet.",
  "agentLive.queue": "Queue",
  "agentLive.uptime": "Uptime",
  "agentLive.lastEvent": "Last event",
  "agentLive.eventsReceived": "events received",
  "agentLive.forbidden": "No permission for Agent Live.",
  "agentLive.killfeed": "Killfeed",
  "agentLive.noKills": "No kills received",
  "agentLive.kfHeadshot": "Headshot",
  "agentLive.kfSuicide": "Suicide",
  "agentLive.kfEnvironment": "Environment",
  "agentLive.kfUnknownWeapon": "Unknown weapon",
  "killfeedPanel.title": "Killfeed",
  "killfeedPanel.empty": "No killfeed events yet",
  "killfeedPanel.offline": "Agent offline – no live killfeed",
  "killfeedPanel.reset": "Reset position",
  "killfeedPanel.collapse": "Collapse",
  "killfeedPanel.expand": "Expand",
  "dashboard.agent": "Agent",
  "dashboard.agentNone": "No agent connected",
  "nav.log": "Log",
  "nav.auditLog": "Audit Log",
  "nav.users": "Users & Roles",
  "nav.ingameAdmins": "Ingame Admins",
  "nav.servers": "Servers",
  "nav.administration": "Administration",

  // App shell / topbar
  "shell.commandTerminal": "Command Terminal",
  "shell.rconControl": "RCON Control",
  "shell.menu": "Menu",
  "shell.noServer": "— no server —",
  "shell.offlineSuffix": "offline",
  "shell.connected": "Connected",
  "shell.connecting": "Connecting…",
  "shell.offline": "Offline",
  "shell.logout": "Logout",
  "shell.theme": "Switch theme",
  "shell.themeDark": "Dark",
  "shell.themeLight": "Light",
  "theme.dark": "Dark",
  "theme.light": "Light",
  "theme.bloodForest": "Blood Forest",
  "theme.aurora": "Aurora",
  "theme.synth": "Synth",
  "shell.language": "Language",
  "shell.comingSoonTitle": "This view is coming in a future slice.",
  "shell.comingSoon": "Coming soon.",

  // Common buttons / vocabulary
  "common.save": "Save",
  "common.refresh": "Refresh",
  "common.add": "Add",
  "common.remove": "Remove",
  "common.reload": "Reload",
  "common.search": "Search",
  "common.cancel": "Cancel",
  "common.close": "Close",
  "common.online": "Online",
  "common.offline": "Offline",
  "common.live": "Live",
  "common.auto": "Auto",
  "common.players": "Players",
  "common.map": "Map",
  "common.mode": "Mode",
  "common.size": "Size",
  "common.lastSignal": "Last Signal",
  "common.loading": "Loading…",
  "common.none": "None",

  // Login
  "login.secureTerminal": "SECURE TERMINAL · 2142",
  "login.subtitle": "BATTLEFIELD 2142 · NORTHERN STRIKE · RCON",
  "login.user": "Username",
  "login.password": "Password",
  "login.submit": "Sign in",
  "login.errorFailed": "Sign-in failed",
  "login.errorUnreachable": "Service unreachable",

  // Dashboard
  "dashboard.title": "Dashboard",
  "dashboard.serversInScope": "servers in scope · tiles customizable",
  "dashboard.edit": "Customize",
  "dashboard.editDone": "Done",
  "dashboard.hostTelemetry": "Host Telemetry",
  "dashboard.liveCacheMatrix": "Live Cache Matrix",
  "dashboard.cpuLoad": "CPU / Load",
  "dashboard.ram": "RAM",
  "dashboard.disk": "Disk",
  "dashboard.rconQpm": "RCON QPM",
  "dashboard.unavailable": "unavailable",
  "dashboard.colServer": "Server",
  "dashboard.colState": "State",
  "dashboard.colMapMode": "Map / Mode",
  "dashboard.colPlayers": "Players",
  "dashboard.colCacheAge": "Cache Age",
  "dashboard.colRconQpm": "RCON QPM",
  "dashboard.colRconHealth": "RCON Health",
  "dashboard.colLastSignal": "Last Signal",
  "dashboard.noServers": "No servers in scope.",
  "dashboard.loadingHost": "Loading control host telemetry.",
  "dashboard.gameHostNotConnected": "Game server host metrics: not connected",

  // Central view titles
  "view.players": "Players",
  "view.playerIntel": "Player Intel",
  "view.playerSearch": "Player Search",
  "view.playerDetail": "Player Detail",
  "view.maps": "Maps",
  "view.auditLog": "Audit Log",
  "view.users": "Users & Roles",
  "view.servers": "Servers",
  "view.noServerSelected": "No server selected.",

  // Common vocabulary (Maps/Players)
  "common.error": "Error",
  "common.apply": "Apply",
  "common.set": "Set",
  "common.load": "Load",
  "common.command": "Command",
  "field.rank": "Rank",
  "field.player": "Player",
  "field.action": "Action",
  "field.actions": "Actions",
  "status.on": "ON",
  "status.off": "OFF",

  // Maps view
  "maps.sub": "Map list, round control, tickets & flag-by-flag.",
  "maps.toastRestarted": "Map restarted",
  "maps.nextMap": "Next map",
  "maps.toastNextLoaded": "Next map loaded",
  "maps.currentMaplist": "Current map list",
  "maps.emptyRotation": "No maps in rotation. Add one below.",
  "maps.rawOutput": "Raw output",
  "maps.toastSaved": "Map list saved",
  "maps.toastTicketsSet": "Tickets set",
  "maps.toastTicketsDoubled": "Tickets doubled",
  "maps.doubleTitle": "Double both teams",
  "maps.fbfOn": "Enable",
  "maps.fbfOff": "Disable",
  "maps.fbfEnabled": "Flag-by-flag on",
  "maps.fbfDisabled": "Flag-by-flag off",
  "maps.addMap": "Add map",
  "maps.append": "Append",
  "maps.toastMapAdded": "Map added (don't forget mapList.save)",
  "maps.toastRemoved": "Entry removed (don't forget mapList.save)",
  "maps.loadNow": "Load now",
  "maps.loadNowTitle": "Switch to this map immediately (ends the current round)",
  "maps.toastPickMap": "Pick a map first",
  "maps.switchTo": "Switch now to",
  "maps.roundEnds": "The current round will end.",
  "maps.switching": "Switching to",
  "maps.setNextMap": "Set next map",
  "maps.nextMapIndex": "Next map = index",
  "maps.removeEntry": "Remove entry",

  // Players view
  "players.empty": "No players.",
  "players.kickTitle": "Kick player",
  "players.banTitle": "Ban player",
  "players.banEngine": "ban via server/engine ban",
  "players.slotId": "Slot ID",
  "players.copy": "copy",
  "players.copied": "copied",
  "players.copyFailed": "could not be copied",
  "players.kickSent": "Kick sent",
  "players.banSent": "Ban sent",
  "players.kickFailed": "Kick failed",
  "players.banFailed": "Ban failed",
  "players.warn": "Warn",
  "players.warnTitle": "Warn player",
  "players.warnPrompt": "Warning to",
  "players.warnSent": "Warning sent",
  "players.warnFailed": "Warn failed",
  "players.banNoPid": "No PID — ban not possible here",
  "players.banPidConfirm": "ban via PID ban? (panel-enforced, kick-on-join)",
  "players.banReasonPrompt": "Reason (optional):",
  "players.autobalance": "Autobalance",
  "players.abOn": "Autobalance on",
  "players.abOff": "Autobalance off",
  "players.noLiveData": "No live data — server/worker not connected.",
  "players.of": "of",
  "players.statTickets": "Tickets",
  "players.tacControls": "Tactical map controls",
  "players.tacObjects": "Objects",
  "players.tacCalibrate": "Calibrate",
  "players.tacFitTitle": "Auto-zoom to objects",
  "players.tacObjectsTitle": "Toggle static objects",
  "players.tacCropTitle": "Drag crop area in the editor",
  "players.tacCalibrateTitle": "Calibrate 3 points in the editor",
  "players.tacEditorTitle": "Open the large calibration editor",
  "players.tacCloseTitle": "Close (ESC)",
  "players.tacCropHint": "Crop: drag a rectangle over the area (auto-saved); tiny drag = reset",
  "players.tacCropEnd": "Crop editor closed",
  "players.tacCalHint": "Calibration: stand on a point in-game, then click here (3×)",
  "players.tacCalCancel": "Calibration cancelled",
  "players.tacCropSaved": "Crop saved ✓",
  "players.tacCropReset": "Crop reset",
  "players.tacSaveFailed": "Save failed",
  "players.tacPosFrom": "Position of",
  "players.tacPosCaptured": "captured",
  "players.tacNoWorld": "No world coordinates",
  "players.tacCalSaved": "Calibration saved ✓",
  "players.kickVerb": "kick",
  "players.tacCropStatus": "CROP — drag a rectangle over the area · tiny drag = reset",
  "players.tacCalStatus": "CALIBRATE — stand on a spot in-game, then click · point",
  "players.tacPoint": "Point",
  "players.tacWhoOnSpot": "who is standing on this spot?",
  "players.tacPickNumber": "Number — or X,Z manually:",
  "players.tacNoLiveCoords": "no live coordinates.",
  "players.tacManualXZ": "World X,Z manually (e.g. \"358,50\"):",
  "players.tacFitFlagTitle": "Fit shows the whole object bbox – turn off for the wide crop",

  // Shared vocabulary (Player Intel / Audit)
  "common.reset": "Reset",
  "status.visible": "visible",
  "status.hidden": "hidden",

  // Player Intel
  "playerIntel.sub": "Persistent player history from live polls, chat history and the GeoIP cache. IP/Geo stay permission-gated.",
  "playerIntel.searchPlaceholder": "Search PID, name or clan tag",
  "playerIntel.searchPlaceholderIp": "Search PID, name, clan tag or IP",
  "playerIntel.loadingList": "Loading player history…",
  "playerIntel.noPlayers": "No players found.",
  "playerIntel.noTag": "NO TAG",
  "playerIntel.seenLower": "seen",
  "playerIntel.loadFailed": "Could not load Player Intel.",
  "playerIntel.detailLoadFailed": "Could not load player.",
  "playerIntel.selectPlayer": "Select a player to inspect history and links.",
  "playerIntel.lastServer": "Last server",
  "playerIntel.lastSeen": "Last seen",
  "playerIntel.firstSeen": "First seen",
  "playerIntel.seen": "Seen",
  "playerIntel.seenCount": "Seen count",
  "playerIntel.vehicle": "Vehicle",
  "playerIntel.kit": "Kit",
  "playerIntel.current": "Current",
  "playerIntel.nameHistory": "Name history",
  "playerIntel.clanTagHistory": "ClanTag history",
  "playerIntel.serverHistory": "Server history",
  "playerIntel.ipHistory": "IP history",
  "playerIntel.chatHistory": "Chat history",
  "playerIntel.noStoredPre": "No",
  "playerIntel.noStoredPost": "stored.",
  "playerIntel.noServerHistory": "No server history stored.",
  "playerIntel.noIpHistory": "No IP history stored.",
  "playerIntel.noChatLines": "No chat lines linked.",
  "playerIntel.ipRequiresPerm": "IP history requires action.view_ip.",
  "playerIntel.chatRequiresPerm": "Chat history requires view.chat_history.",
  "playerIntel.requiresViewGeo": "requires action.view_geo",
  "playerIntel.geoNotConfigured": "GeoIP not configured",
  "playerIntel.geoDbMissing": "GeoIP DB missing",
  "playerIntel.refreshGeo": "Refresh GeoIP",
  "playerIntel.geoLooking": "Resolving GeoIP…",

  // Audit Log
  "audit.sub": "Database-backed audit log (auth, rcon, bans, settings …) — separate from the app/file log in the Log tab.",
  "audit.filter": "Filter",
  "audit.events": "Events",
  "audit.entry": "Entry",
  "audit.selectRow": "Select a row to inspect details.",
  "audit.time": "Time",
  "audit.result": "Result",
  "audit.actor": "Actor",
  "audit.target": "Target",
  "audit.summary": "Summary",
  "audit.serverId": "Server ID",
  "audit.range": "Time range",
  "audit.textSearch": "Text search (client)",
  "audit.actionPlaceholder": "e.g. auth.login",
  "audit.actorPlaceholder": "Username",
  "audit.serverIdPlaceholder": "e.g. ns-ranked",
  "audit.textPlaceholder": "Action, actor, target, summary…",
  "audit.all": "All",
  "audit.range1h": "1 h",
  "audit.range24h": "24 h",
  "audit.range7d": "7 days",
  "audit.range30d": "30 days",
  "audit.loading": "Loading audit log…",
  "audit.empty": "No entries.",
  "audit.loadFailed": "Could not load the audit log.",
  "audit.noMetadata": "No metadata.",
  "audit.sensitiveHintPre": "IP / User-Agent require action.view_sensitive. Sensitive values are marked server-side as",
  "audit.sensitiveHintPost": ".",

  // Shared vocabulary (final view sweep)
  "common.send": "Send",
  "common.clear": "Clear",
  "common.execute": "Execute",
  "common.editAction": "Edit",
  "common.delete": "Delete",
  "common.saved": "Saved",
  "common.deleted": "Deleted",
  "common.noChanges": "No changes",
  "field.name": "Name",
  "field.reason": "Reason",

  // Log
  "log.sub": "Last 250 logged actions.",

  // Console
  "console.sub": "Free RCON command (exec). Destructive commands are admin-only.",
  "console.ready": "Ready. Enter a command below…",
  "console.noOutput": "(no output)",

  // Chat
  "chat.sub": "Live server chat (via the worker). Broadcasts to all/team.",
  "chat.history": "History",
  "chat.empty": "No chat lines yet. (The worker must be running.)",
  "chat.messagePlaceholder": "Message…",
  "chat.toTeam": "To team",
  "chat.toAll": "To all",
  "chat.broadcastSent": "Broadcast sent",
  "chat.sentToTeam": "Sent to team",

  // Announcer
  "announcer.sub": "Rotating game.sayAll messages.",
  "announcer.messages": "Messages",
  "announcer.addMessage": "+ Message",
  "announcer.empty": "No messages yet.",
  "announcer.maxHint": "Max. 20 messages; empty ones are removed on save.",
  "announcer.enabled": "Enabled",
  "announcer.intervalLabel": "Interval (seconds, min. 10)",
  "announcer.saved": "Announcer saved",
  "nav.joinAnnouncer": "Join Announcer",
  "joinAnnouncer.sub": "Configure per-server join/leave announcements (game.sayAll).",
  "joinAnnouncer.settings": "Settings",
  "joinAnnouncer.templates": "Templates",
  "joinAnnouncer.variables": "Variables",
  "joinAnnouncer.variablesHint": "Usable inside templates. Missing values use sensible defaults.",
  "joinAnnouncer.default": "Default",
  "joinAnnouncer.preview": "Preview",
  "joinAnnouncer.previewJoin": "Join:",
  "joinAnnouncer.previewLeave": "Leave:",
  "joinAnnouncer.previewEmpty": "Nothing to show.",
  "joinAnnouncer.previewNote": "Preview with a sample player. The worker picks up changes within 30 seconds.",
  "joinAnnouncer.joinEnabled": "Join announcements on",
  "joinAnnouncer.leaveEnabled": "Leave announcements on",
  "joinAnnouncer.statsEnabled": "Stats lines on",
  "joinAnnouncer.colorEnabled": "Color on",
  "joinAnnouncer.cooldown": "Cooldown (seconds)",
  "joinAnnouncer.cooldownHint": "Prevents spam on reconnect/refresh (per PID).",
  "joinAnnouncer.joinLine1": "Join line 1",
  "joinAnnouncer.joinLine2": "Join line 2 (stats)",
  "joinAnnouncer.joinLine3": "Join line 3 (stats)",
  "joinAnnouncer.leaveTemplate": "Leave message",
  "joinAnnouncer.statsLineHint": "Lines 2/3 only when stats on AND available — otherwise just line 1.",
  "joinAnnouncer.joinOffOrEmpty": "Join off or lines empty.",
  "joinAnnouncer.leaveOffOrEmpty": "Leave off or message empty.",
  "joinAnnouncer.reset": "Reset",
  "joinAnnouncer.retry": "Retry",
  "joinAnnouncer.loading": "Loading settings…",
  "joinAnnouncer.loadError": "Failed to load settings.",
  "joinAnnouncer.readonly": "Read-only (missing action.edit_announcer) — saving disabled.",
  "joinAnnouncer.saved": "Join announcer saved",

  // Commands
  "commands.sub": "Predefined command groups. Toggles remember autoload for map start.",
  "commands.autoTitle": "Re-apply automatically on map start",
  "commands.confirmRunPre": "Run \"",
  "commands.confirmRunPost": "\"?",
  "commands.sentSuffix": "sent",
  "commands.commandsWord": "command(s)",
  "commands.loading": "Loading command groups…",
  "commands.loadError": "Failed to load command groups.",
  "commands.retry": "Retry",
  "commands.empty": "No command groups configured.",

  // Settings
  "settings.sub": "sv.* server settings (live, admins only).",
  "settings.savedCountSuffix": "setting(s) saved",

  // Bans
  "bans.sub": "Panel PID bans, known PIDs and separate engine/RCON ban lists.",
  "bans.pidBanExplanation": "PID bans are enforced by the panel/worker. If this PID appears online, the player is kicked automatically.",
  "bans.activePidBans": "Active PID Bans",
  "bans.panelWorkerEnforced": "Panel / Worker Enforced",
  "bans.reasonRequiredPh": "Reason required",
  "bans.noPidBans": "No PID bans.",
  "bans.by": "By",
  "bans.created": "Created",
  "bans.knownPids": "Known PIDs",
  "bans.knownPidsOffline": "Known PIDs / Offline Ban",
  "bans.knownNote": "Source: seen_players. Search by name or PID; IP search is intentionally not included here.",
  "bans.searchNamePid": "Search name or PID…",
  "bans.noResults": "No results.",
  "bans.lastSeenShort": "Last",
  "bans.hitsSuffix": "hits",
  "bans.engineRconIp": "Engine / RCON IP",
  "bans.engineRconBanIp": "Engine / RCON Ban: IP",
  "bans.engineNote": "RCON/server-side address ban list. Not the same as a panel/worker-enforced PID ban.",
  "bans.ipAddress": "IP address",
  "bans.ban": "Ban",
  "bans.unban": "Unban",
  "bans.ingameAdminsTitle": "Ingame admins",
  "bans.legacyWhitelistReplaced": "Legacy whitelist has been replaced by Ingame Admins.",
  "bans.ingameAdminsHint": "In-game rights (#k/#w/#b/#ab/…) are managed under \"Administration → Ingame Admins\".",
  "bans.openIngameAdmins": "→ Ingame Admins",
  "bans.pidReasonRequired": "PID and reason required",
  "bans.pidBanned": "PID banned",
  "bans.pidUnbanned": "PID unbanned",
  "bans.offlineBanSet": "Offline PID ban set",
  "bans.ipBanned": "IP banned",
  "bans.ipUnbanned": "IP unbanned",
  "bans.confirmRemovePidPre": "Remove PID ban",
  "bans.confirmRemovePidPost": "?",
  "bans.banReasonPrompt": "Ban reason for",
  "bans.banReasonPromptSuffix": "(required):",
  "bans.confirmCreatePre": "Create PID ban for",
  "bans.confirmCreatePost": "?",
  "bans.reasonLabel": "Reason",

  // Servers
  "servers.sub": "Manage RCON access. Passwords are never shown.",
  "servers.addServer": "+ New server",
  "servers.newServer": "New server",
  "servers.editSuffix": "edit",
  "servers.pwSet": "set",
  "servers.pwMissing": "missing",
  "servers.hostIp": "Host / IP",
  "servers.rconPassword": "RCON password",
  "servers.emptyUnchanged": "(empty = unchanged)",
  "servers.idAutoHint": "The server ID is generated automatically from the name.",
  "servers.confirmDeletePre": "Delete server \"",
  "servers.confirmDeletePost": "\"? Assignments will be removed.",

  // Users
  "users.sub": "Accounts, roles and server assignment.",
  "usersRoles.sub": "Accounts, roles and permissions.",
  "usersRoles.tabUsers": "Users",
  "usersRoles.tabRoles": "Roles",
  "usersRoles.tabPermissions": "Permissions",
  "usersRoles.usersTitle": "Users",
  "usersRoles.rolesTitle": "Roles",
  "usersRoles.permissionsTitle": "Permission catalog",
  "usersRoles.viewPermissions": "Permissions",
  "usersRoles.effectiveTitle": "Effective permissions",
  "usersRoles.legacyAdminNote": "Legacy admin role grants all permissions",
  "usersRoles.granularRoles": "Granular roles",
  "usersRoles.noRoles": "No roles assigned",
  "usersRoles.viewerFallback": "No roles → conservative viewer fallback",
  "usersRoles.directOverrides": "Direct overrides",
  "usersRoles.effectivePermissions": "Effective permissions",
  "usersRoles.roleName": "Role",
  "usersRoles.userCount": "Users",
  "usersRoles.permissionCount": "Perms",
  "usersRoles.rolesReadonlyNote": "Click a role to edit its permissions.",
  "usersRoles.permissionsReadonlyNote": "Catalog view only — assignment via roles/users.",
  "usersRoles.noPassword": "no password",
  "usersRoles.matrixTitle": "Permission matrix",
  "usersRoles.selectRoleHint": "Click a role in the list to view/edit its permissions.",
  "usersRoles.systemRoleNote": "System role — not editable.",
  "usersRoles.noManageRoles": "Read-only — missing permission (action.manage_roles).",
  "usersRoles.savePermissions": "Save permissions",
  "usersRoles.unsavedChanges": "Unsaved changes",
  "usersRoles.noChanges": "No changes",
  "usersRoles.permsSaved": "Permissions saved",
  "usersRoles.added": "added",
  "usersRoles.removed": "removed",
  "usersRoles.assignRolesTitle": "Role assignment",
  "usersRoles.legacyRole": "Legacy role",
  "usersRoles.legacyAdminAssignNote": "Legacy admin has all permissions. Assigned roles do not restrict this user.",
  "usersRoles.needBothManage": "Requires action.manage_users and action.manage_roles.",
  "usersRoles.systemBadge": "System role",
  "usersRoles.systemRolesNotAssignable": "System roles cannot be assigned here.",
  "usersRoles.saveRoles": "Save roles",
  "usersRoles.rolesSaved": "Roles saved",
  "usersRoles.noAssignableRoles": "No assignable roles.",
  "usersRoles.resetPassword": "Reset password",
  "usersRoles.resetConfirmHint": "The temporary password will only be shown once and remains valid until it is changed or reset again.",
  "usersRoles.resetConfirmAction": "Reset",
  "usersRoles.pwResetDone": "Password reset",
  "usersRoles.pwResetOnce": "This password will not be shown again. It remains valid until it is changed or reset again.",
  "usersRoles.copy": "Copy",
  "usersRoles.copied": "Copied",
  "usersRoles.copyManual": "Please select and copy manually.",
  "usersRoles.tempPasswordTitle": "Temporary password",
  "usersRoles.createUser": "Create user",
  "usersRoles.createSubmit": "Create",
  "usersRoles.optional": "(optional)",
  "usersRoles.userCreated": "User created",
  "usersRoles.createHint": "A temporary password is generated automatically. It will only be shown once and remains valid until it is changed or reset again.",
  "users.addUser": "+ New user",
  "users.newUser": "New user",
  "users.editSuffix": "edit",
  "users.role": "Role",
  "users.you": " · you",
  "users.allServers": "all servers",
  "users.nServersSuffix": "servers",
  "users.displayName": "Display name",
  "users.password": "Password",
  "users.serverAccess": "Server access",
  "users.adminAllServers": "Admins have access to all servers.",
  "users.confirmDeletePre": "Delete account \"",
  "users.confirmDeletePost": "\"?",

  // Ingame Admins (PID-based ingame moderators, no web login)
  "ingameAdmins.title": "Ingame Admins",
  "ingameAdmins.sub": "PID-based ingame moderators (no web login). Permissions apply ingame via chat commands.",
  "ingameAdmins.list": "Ingame Admins",
  "ingameAdmins.empty": "No ingame admins yet.",
  "ingameAdmins.add": "+ New ingame admin",
  "ingameAdmins.new": "New ingame admin",
  "ingameAdmins.editSuffix": "edit",
  "ingameAdmins.label": "Name / Label",
  "ingameAdmins.role": "Role",
  "ingameAdmins.scope": "Server scope",
  "ingameAdmins.permissions": "Permissions",
  "ingameAdmins.status": "Status",
  "ingameAdmins.lastSeen": "Last seen",
  "ingameAdmins.active": "Active",
  "ingameAdmins.inactive": "Inactive",
  "ingameAdmins.allServers": "All servers",
  "ingameAdmins.nServersSuffix": "servers",
  "ingameAdmins.enabled": "Enabled",
  "ingameAdmins.notes": "Notes",
  "ingameAdmins.scopeAll": "All servers",
  "ingameAdmins.scopeSpecific": "Specific servers",
  "ingameAdmins.disable": "Disable",
  "ingameAdmins.enable": "Enable",
  "ingameAdmins.rolePreset": "Role preset",
  "ingameAdmins.roleHelper": "Helper",
  "ingameAdmins.roleModerator": "Moderator",
  "ingameAdmins.roleAdmin": "Admin",
  "ingameAdmins.roleCustom": "Custom",
  "ingameAdmins.permWarn": "Warn",
  "ingameAdmins.permSay": "Say",
  "ingameAdmins.permKick": "Kick",
  "ingameAdmins.permTempBan": "Temp ban",
  "ingameAdmins.permPidBan": "PID ban",
  "ingameAdmins.permAutobalance": "Autobalance",
  "ingameAdmins.permRestart": "Map restart",
  "ingameAdmins.permNextMap": "Next map",
  "ingameAdmins.permTickets": "Set tickets",
  "ingameAdmins.permFbf": "Flag-by-Flag",
  "ingameAdmins.permDoubleTickets": "Double tickets",
  "ingameAdmins.import": "Import legacy",
  "ingameAdmins.importTitle": "Import chat_admins / ban_admins → ingame admins",
  "ingameAdmins.toastSaved": "Ingame admin saved",
  "ingameAdmins.toastDeleted": "Ingame admin deleted",
  "ingameAdmins.toastEnabled": "Ingame admin enabled",
  "ingameAdmins.toastDisabled": "Ingame admin disabled",
  "ingameAdmins.toastImported": "Import complete",
  "ingameAdmins.confirmDeletePre": "Delete ingame admin PID ",
  "ingameAdmins.confirmDeletePost": "?",
  "ingameAdmins.confirmImport": "Import legacy chat_admins/ban_admins now? (idempotent, will not overwrite existing entries)",
  "ingameAdmins.pidPlaceholder": "PID (digits only)",
  "ingameAdmins.labelPlaceholder": "e.g. clan tag or real name",
  // Datei-Explorer
  "nav.files": "Files",
  "files.title": "Files",
  "files.sub": "Gameserver files via agent or SFTP – read everywhere, write only in allowed folders",
  "files.server": "Server",
  "files.noServers": "No agent server with file explorer available (agent config: fs_enabled).",
  "files.agentOffline": "Agent not connected",
  "files.readOnly": "Read only",
  "files.writable": "Writable",
  "files.writeDirs": "Allowed folders",
  "files.noWriteDirs": "None – the explorer is fully read-only",
  "files.name": "Name",
  "files.modified": "Modified",
  "files.empty": "Folder is empty",
  "files.truncated": "List truncated – too many entries",
  "files.up": "Up one level",
  "files.newFolder": "New folder",
  "files.newFolderPrompt": "Name of the new folder:",
  "files.uploadFiles": "Upload files",
  "files.uploadFolder": "Upload folder",
  "files.overwrite": "Overwrite existing",
  "files.uploadDisabled": "Upload not allowed here – only in allowed folders",
  "files.noWritePermission": "No write permission (action.files_write)",
  "files.dropHint": "Drop files here to upload",
  "files.open": "Open",
  "files.download": "Download",
  "files.extract": "Extract",
  "files.extractConfirm": "Extract ZIP into this folder?",
  "files.rename": "Rename",
  "files.renamePrompt": "New name:",
  "files.deleteConfirmPre": "Really delete: ",
  "files.deleteConfirmDirSuffix": " (folder including all contents)",
  "files.unsaved": "unsaved",
  "files.readOnlyFile": "read-only",
  "files.discardConfirm": "Discard unsaved changes?",
  "files.saved": "Saved",
  "files.transfers": "Transfers",
  "files.statusUploading": "uploading",
  "files.statusTransferring": "transferring to server",
  "files.statusDownloading": "fetching from server",
  "files.statusDone": "done",
  "files.statusFailed": "upload/download failed",
  "files.clearDone": "Hide finished",
  "files.link": "Link",
  "files.extracted": "Extracted",
  "files.created": "Created",
  "files.renamed": "Renamed",
  "files.deleted": "Deleted",
  "files.viaAgent": "via agent",
  "files.viaSftp": "via SFTP (fallback)",
  "files.viaSftpHint": "Agent not connected – access goes through the SFTP container",
  "files.sftpConfigErrors": "SFTP configuration invalid",
};

/** Alle Wörterbücher (für Tests / Vollständigkeitsprüfung). */
export const dictionaries: Record<Lang, Record<TranslationKey, string>> = { de, en };

/* ------------------------------------------------------------------ Laufzeit */

/** Ermittelt die Startsprache: localStorage > Browser (en* → EN) > DE. */
export function detectLang(): Lang {
  try {
    const stored = localStorage.getItem(LANG_KEY);
    if (stored === "de" || stored === "en") return stored;
  } catch {
    /* localStorage evtl. nicht verfügbar */
  }
  try {
    if (typeof navigator !== "undefined" && navigator.language?.toLowerCase().startsWith("en")) {
      return "en";
    }
  } catch {
    /* navigator evtl. nicht verfügbar */
  }
  return "de";
}

/** Aktives Sprach-Signal (reaktiv). */
export const lang = signal<Lang>(detectLang());

/** Sprache setzen + persistieren. Unbekannte Werte werden ignoriert (Fallback bleibt). */
export function setLanguage(next: Lang): void {
  if (next !== "de" && next !== "en") return;
  lang.value = next;
  try {
    localStorage.setItem(LANG_KEY, next);
  } catch {
    /* best-effort */
  }
}

/** Übersetzt einen Key in der aktiven Sprache (Fallback: DE, dann der Key selbst). */
export function t(key: TranslationKey): string {
  const table = dictionaries[lang.value] ?? de;
  return table[key] ?? de[key] ?? key;
}

/** Hook-Stil-Zugriff (Komponenten können `const { t } = useI18n()` nutzen). */
export function useI18n(): { t: typeof t; lang: Lang; setLanguage: typeof setLanguage } {
  return { t, lang: lang.value, setLanguage };
}
